import { Outlet, useLocation, useNavigate } from "react-router-dom";

export const REVENUE_TABS = [
  { name: "Revenue Report", href: "reports" },
  { name: "Businesses", href: "businesses" },
  { name: "Shippers", href: "shippers" },
  { name: "Revenue Settings", href: "revenue-settings" },
];

// First URL segment after /revenue -> the sidebar tab that owns that flow
const SECTION_TO_TAB = {
  reports: "reports",
  businesses: "businesses",
  business: "businesses", // /revenue/business/:id/... (payouts, payout request)
  shippers: "shippers",
  shipper: "shippers", // /revenue/shipper/:id/... (payouts, payout request)
  "revenue-settings": "revenue-settings", // includes /revenue-settings/business|shipper/:id
};

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

/**
 * Revenue heading + left navigation shared by every revenue page.
 * Child routes render in the content area.
 */
export default function RevenueLayout() {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  // e.g. /admin/revenue/shipper/12/payouts -> base "/admin/revenue", section "shipper"
  const revenueIndex = pathname.indexOf("/revenue");
  const basePath = pathname.slice(0, revenueIndex + "/revenue".length);
  const section = pathname.slice(basePath.length).split("/")[1] || "reports";
  const activeTab = SECTION_TO_TAB[section] || "reports";

  const goToTab = (tab) => {
    // Also lets you jump back to a tab's list from deeper in its flow
    if (pathname !== `${basePath}/${tab.href}`) {
      navigate(`${basePath}/${tab.href}`);
    }
  };

  return (
    <div data-test="revenue-layout">
      <div className="mb-8">
        <h1 className="text-3xl font-semibold text-gray-900 dark:text-white">
          Revenue
        </h1>
      </div>

      <div className="flex flex-col sm:flex-row gap-5">
        {/* Left Navigation */}
        {/* Sticks just under the admin header (sticky, h-16, z-10) while the page scrolls.
            Phones: pinned horizontal bar with the page background behind it.
            sm+: the sidebar column; self-start so it doesn't stretch to the content height */}
        <div className="sm:w-64 flex-shrink-0 sticky top-16 z-[5] -mx-4 px-4 py-2 sm:mx-0 sm:px-0 sm:py-0 sm:top-20 sm:self-start bg-[#F6F8FF] dark:bg-[#010101] sm:bg-transparent sm:dark:bg-transparent">
          <nav
            className="flex flex-row sm:flex-col space-x-2 sm:space-x-0 sm:space-y-2 overflow-x-auto sm:overflow-x-visible bg-white dark:bg-[#121212] p-4 rounded-lg border border-gray-200 dark:border-gray-700"
            aria-label="Revenue Navigation"
          >
            {REVENUE_TABS.map((tab) => (
              <button
                key={tab.href}
                type="button"
                onClick={() => goToTab(tab)}
                className={classNames(
                  tab.href === activeTab
                    ? "bg-primary text-white"
                    : "text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800",
                  "whitespace-nowrap py-3 px-4 rounded-md font-medium text-sm text-left transition-colors",
                )}
                aria-current={tab.href === activeTab ? "page" : undefined}
              >
                {tab.name}
              </button>
            ))}
          </nav>
        </div>

        {/* Right Content */}
        <div className="flex-1 min-w-0">
          <Outlet />
        </div>
      </div>
    </div>
  );
}
